import { DEFAULT_LOCALE, normalizeLocale } from "./messages"

const STORAGE_KEY = "quizalize.hoopzz-solo.language"
// Per-tab record of an in-play language change (student switched the embedded player mid-game).
// getInitialLocale() prefers it over the launch ?language= URL so a restart (the Play Again button
// runs window.location.reload) keeps the chosen language instead of flashing back to the launch one.
const SESSION_KEY = "quizalize.hoopzz-solo.language.session"
const RTL_LANGUAGES = new Set(["ar", "fa", "he", "ur"])

function getBrowserLocale() {
	if (typeof navigator === "undefined") {
		return null
	}

	return navigator.languages?.[0] || navigator.language || null
}

function getStoredLocale() {
	try {
		return window.localStorage.getItem(STORAGE_KEY)
	} catch (error) {
		return null
	}
}

function storeLocale(locale) {
	try {
		window.localStorage.setItem(STORAGE_KEY, locale)
	} catch (error) {
		// Storage can be disabled in some embedded/private browser contexts.
	}
}

function getSessionLocale() {
	try {
		if (typeof window === "undefined" || !window.sessionStorage) return null
		return window.sessionStorage.getItem(SESSION_KEY)
	} catch (error) {
		return null
	}
}

// Record an in-play language change (when the game follows the player's LANGUAGE_CHANGED) so a
// restart/reload keeps it. Session-scoped: a fresh launch (new tab) has none and honours the URL.
export function persistSessionLocale(locale) {
	try {
		if (typeof window === "undefined" || !window.sessionStorage) return
		window.sessionStorage.setItem(SESSION_KEY, normalizeLocale(locale))
	} catch (error) {
		// sessionStorage can be disabled in some embedded/private browser contexts.
	}
}

export function resolveLocaleFromSearch(search = "") {
	const params = new URLSearchParams(search)
	const searchLocale = params.get("language") || params.get("lang")

	return searchLocale ? normalizeLocale(searchLocale) : null
}

export function getInitialLocale() {
	if (typeof window === "undefined") {
		return DEFAULT_LOCALE
	}

	return normalizeLocale(
		// An in-play choice (this tab/session) wins over the launch ?language= so a restart keeps
		// it with NO flash back to the launch language. A fresh launch has no session locale.
		getSessionLocale() ||
			resolveLocaleFromSearch(window.location.search) ||
			getStoredLocale() ||
			getBrowserLocale() ||
			DEFAULT_LOCALE
	)
}

export function getLanguageDirection(locale) {
	const language = normalizeLocale(locale).split("-")[0].toLowerCase()
	return RTL_LANGUAGES.has(language) ? "rtl" : "ltr"
}

export function applyDocumentLocale(locale) {
	const normalizedLocale = normalizeLocale(locale)
	document.documentElement.lang = normalizedLocale
	// Keep the LAYOUT left-to-right for every language. This is a basketball play-field
	// (court/hoop/ball/aim are positioned in a fixed LTR coordinate system and a clean RTL
	// mirror is not feasible), so only the TEXT content changes per language — Arabic/Hebrew
	// glyphs still render right-to-left within their own boxes via Unicode bidi, but the page
	// is never mirrored. (Intentional: dir is forced to "ltr" rather than getLanguageDirection.)
	document.documentElement.dir = "ltr"
	storeLocale(normalizedLocale)
}

export function withLocaleParam(href, locale) {
	if (!href) {
		return href
	}

	const normalizedLocale = normalizeLocale(locale)

	try {
		const isAbsolute = /^https?:\/\//i.test(href)
		const url = new URL(href, window.location.origin)
		url.searchParams.set("language", normalizedLocale)

		if (isAbsolute) {
			return url.toString()
		}

		return `${url.pathname}${url.search}${url.hash}`
	} catch (error) {
		return href
	}
}
